import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserProfile } from '../store/models/admin.model';

@Injectable({
    providedIn: 'root'
})
export class InternalMyProfileService {
    private apiUrl = `${environment.apiUrl}UserProfile`;
    public profileSubject = new BehaviorSubject<UserProfile | null>(null);
    profile$ = this.profileSubject.asObservable();

    constructor(private http: HttpClient) { }

    /**
     * Call this once (e.g. in app.component or a root guard) to initialize the profile for the current user.
     */
    initProfile(msalService: MsalService) {
        const account = msalService.instance.getActiveAccount();
        if (!account || !account.idTokenClaims["signInNames.emailAddress"]) {
            this.profileSubject.next(null);
            return;
        }
        const email = account.idTokenClaims["signInNames.emailAddress"] as string;
        this.getUserProfile(email).subscribe(profile => {
            if (profile) {
                this.profileSubject.next(profile);
            } else {
                // Create new profile if not found
                const newProfile: UserProfile = {
                    homeAccountId: email,
                    email: email,
                    name: account.idTokenClaims.name || '',
                    address: '',
                    phoneNumber: '',
                    country: '',
                    companyName: '',
                    companyAddress: '',
                    companyPhoneNumber: '',
                    favoriteVehicles: []
                };
                this.createUserProfile(newProfile).subscribe(created => {
                    this.profileSubject.next(created);
                });
            }
        });
    }

    /**
     * Add a vehicle to the user's favorites and update the profile.
     */
    addVehicleToFavorites(vehicle: string) {
        const profile = this.profileSubject.value;
        console.log('[ProfileService] addVehicleToFavorites called with:', vehicle, 'Current profile:', profile);
        if (!profile) return;
        if (!profile.favoriteVehicles.includes(vehicle)) {
            const updatedProfile = {
                ...profile,
                favoriteVehicles: [...profile.favoriteVehicles, vehicle]
            };
            this.updateUserProfile(updatedProfile).pipe(take(1)).subscribe(updated => {
                console.log('[ProfileService] addVehicleToFavorites backend response:', updated);
                if (updated) {
                    this.profileSubject.next(updated);
                    console.log('[ProfileService] addVehicleToFavorites updated profileSubject:', updated);
                }
            });
        }
    }

    /**
     * Remove a vehicle from the user's favorites and update the profile.
     */
    removeVehicleFromFavorites(vehicle: string) {
        const profile = this.profileSubject.value;
        console.log('[ProfileService] removeVehicleFromFavorites called with:', vehicle, 'Current profile:', profile);
        if (!profile) return;
        if (profile.favoriteVehicles.includes(vehicle)) {
            const updatedProfile = {
                ...profile,
                favoriteVehicles: profile.favoriteVehicles.filter(v => v !== vehicle)
            };
            this.updateUserProfile(updatedProfile).pipe(take(1)).subscribe(updated => {
                console.log('[ProfileService] removeVehicleFromFavorites backend response:', updated);
                if (updated) {
                    this.profileSubject.next(updated);
                    console.log('[ProfileService] removeVehicleFromFavorites updated profileSubject:', updated);
                }
            });
        }
    }

    isLoggedIn(): boolean {
        return !!this.profileSubject.value;
    }

    isLoggedIn$: Observable<boolean> = this.profile$.pipe(map(profile => !!profile));

    isAdmin(): boolean {
        const profile = this.profileSubject.value;
        const value = !!profile && typeof profile.email === 'string' && (
            profile.email.toLowerCase().endsWith('volvo.com') ||
            profile.email.toLowerCase().endsWith('office365qavg.com')
        );
        return value;
    }

    getUserProfile(accountId: string): Observable<UserProfile | null> {
        return this.http.get<UserProfile>(`${this.apiUrl}/${accountId}`).pipe(
            // If 404, return null instead of error
            catchError(err => {
                if (err.status === 404) {
                    return of(null);
                }
                return throwError(() => err);
            })
        );
    }

    createUserProfile(profile: UserProfile) {
        return this.http.post<UserProfile>(`${this.apiUrl}`, profile);
    }

    updateUserProfile(profile: UserProfile) {
        return this.http.put<UserProfile>(`${this.apiUrl}`, profile);
    }

    deleteUserProfile(accountId: string) {
        return this.http.delete(`${this.apiUrl}/${accountId}`);
    }

    clearProfile() {
        this.profileSubject.next(null);
    }

    async login(msalService: MsalService): Promise<void> {
        await msalService.instance.loginPopup();
        this.initProfile(msalService);
    }

    async logout(msalService: MsalService): Promise<void> {
        await msalService.instance.logoutPopup();
        this.clearProfile();
    }

    updateProfileAndEmit(profile: UserProfile) {
        this.updateUserProfile(profile).pipe(take(1)).subscribe(updated => {
            if (updated) {
                this.profileSubject.next(updated);
            }
        });
    }
}
